import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Alert, Button, Card, Grid, message, Pagination, Space, Spin, Typography} from 'antd';
import {ClockCircleOutlined} from '@ant-design/icons';
import {useLocation, useNavigate} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';
import ReadLaterService, {ReadLaterItem} from '../service/readLaterService';
import ReadLaterItemCard from '../components/News/ReadLaterItemCard';
import {cacheManager, TTL_PRESETS} from '../utils/cacheManager';
import '../styles/NewsDetail.css';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const ReadLaterPage: React.FC = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const screens = useBreakpoint();
    const [readLaterItems, setReadLaterItems] = useState<ReadLaterItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
    });
    
    // State preservation
    const stateKey = `readLater_${location.pathname}`;
    const hasRestoredState = useRef(false);
    const isRestoringScroll = useRef(false);
    const lastScrollTime = useRef(0);
    const hasManuallyChangedPage = useRef(false);

    const fetchReadLaterItems = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await ReadLaterService.getReadLaterItems(
                pagination.page,
                pagination.pageSize
            );
            setReadLaterItems(response.items);
            setPagination({
                page: response.page,
                pageSize: response.pageSize,
                total: response.total,
                totalPages: response.totalPages,
            });
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.pageSize]);

    // Restore state on mount
    useEffect(() => {
        // Use cacheManager instead of sessionStorage - TTL is handled automatically
        const savedState = cacheManager.get<any>(stateKey);

        if (savedState && !hasRestoredState.current) {
            // Cache manager already validated TTL, so if we get data it's fresh
            if (savedState.readLaterItems && savedState.readLaterItems.length > 0) {
                setReadLaterItems(savedState.readLaterItems || []);
                setPagination(savedState.pagination || {
                    page: 1,
                    pageSize: 10,
                    total: 0,
                    totalPages: 0,
                });
                setError(savedState.error || null);
                setLoading(false); // Important: Set loading to false when restoring state
                hasRestoredState.current = true;
                hasManuallyChangedPage.current = false; // Reset manual page change flag

                // Restore scroll position
                let targetScrollPosition = savedState.scrollPosition;

                if (targetScrollPosition && (targetScrollPosition.x !== 0 || targetScrollPosition.y !== 0)) {
                    const timeSinceLastScroll = Date.now() - lastScrollTime.current;
                    if (timeSinceLastScroll > 1000) {
                        isRestoringScroll.current = true;

                        const restoreScroll = () => {
                            if (isRestoringScroll.current) {
                                window.scrollTo({
                                    left: targetScrollPosition.x,
                                    top: targetScrollPosition.y
                                });
                            }
                        };

                        // Use requestAnimationFrame for optimal scroll restoration
                        const performRestoration = () => {
                            restoreScroll();
                            requestAnimationFrame(() => {
                                isRestoringScroll.current = false;
                            });
                        };

                        restoreScroll();
                        requestAnimationFrame(performRestoration);
                    }
                }

                return; // Don't fetch new data if we restored state
            } else {
                cacheManager.remove(stateKey);
            }
        }
        
        // Always fetch fresh data if no valid state was restored
        if (!hasRestoredState.current && token) {
            fetchReadLaterItems();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateKey, token]);

    // Save state continuously and before unmount
    useEffect(() => {
        const saveState = () => {
            const currentScrollPosition = {
                x: window.scrollX,
                y: window.scrollY
            };
            
            const stateToSave = {
                readLaterItems,
                pagination,
                error,
                scrollPosition: currentScrollPosition,
            };

            // Use cacheManager with 5 minute TTL for read later state
            cacheManager.set(stateKey, stateToSave, { ttl: TTL_PRESETS.MEDIUM });
        };

        // Save state when readLaterItems, pagination, or error changes
        if (readLaterItems.length > 0) {
            saveState();
        }

        // Also save state before unmount using requestAnimationFrame
        return () => {
            requestAnimationFrame(() => {
                saveState();
            });
        };
    }, [readLaterItems, pagination, error, stateKey]);

    // Save scroll position on scroll events (debounced)
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        let lastSavedScrollPosition = { x: 0, y: 0 };
        
        const handleScroll = () => {
            // Update last scroll time
            lastScrollTime.current = Date.now();
            
            // Don't save scroll position if we're currently restoring
            if (isRestoringScroll.current) {
                return;
            }
            
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                const currentState = cacheManager.get<any>(stateKey);
                if (currentState) {
                    const newScrollPosition = {
                        x: window.scrollX,
                        y: window.scrollY
                    };

                    // Only update if scroll position actually changed significantly
                    if (Math.abs(newScrollPosition.y - lastSavedScrollPosition.y) > 10) {
                        currentState.scrollPosition = newScrollPosition;
                        // Re-save with same TTL (5 minutes)
                        cacheManager.set(stateKey, currentState, { ttl: TTL_PRESETS.MEDIUM });
                        lastSavedScrollPosition = newScrollPosition;
                    }
                }
            }, 10);
        };

        window.addEventListener('scroll', handleScroll);
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(timeoutId);
        };
    }, [stateKey]);

    // Handle page changes - fetch when page changes, but respect state restoration
    useEffect(() => {
        if (token) {
            // Only fetch if we're not currently restoring state, or if it's a manual page change
            if (!hasRestoredState.current || hasManuallyChangedPage.current) {
                fetchReadLaterItems();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page, token]);

    const handleMarkAsRead = async (item: ReadLaterItem, isRead: boolean) => {
        try {
            await ReadLaterService.markAsRead(item.article_id, { is_read: isRead });
            message.success(`Article marked as ${isRead ? 'read' : 'unread'}`);
            await fetchReadLaterItems();
        } catch (err: any) {
            message.error(err.message);
        }
    };

    const handleRemoveFromReadLater = async (item: ReadLaterItem) => {
        try {
            await ReadLaterService.removeFromReadLater(item.article_id);
            message.success('Article removed from read later');
            await fetchReadLaterItems();
        } catch (err: any) {
            message.error(err.message);
        }
    };

    const handlePageChange = (page: number) => {
        hasManuallyChangedPage.current = true;
        setPagination(prev => ({ ...prev, page }));
    };



    if (!token) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Alert
                    message="Authentication Required"
                    description="Please log in to view your read later articles."
                    type="info"
                    showIcon
                />
            </div>
        );
    }

    if (loading) {
        return (
            <div style={{ 
                padding: screens.md ? '24px' : '16px', 
                textAlign: 'center' 
            }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px' }}>
                    <Text>Loading your read later articles...</Text>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ 
                padding: screens.md ? '24px' : '16px', 
                maxWidth: screens.md ? '800px' : '100%', 
                margin: '0 auto' 
            }}>
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    return (
        <div style={{ padding: screens.md ? '5px' : '0' }}>
            <div style={{
                textAlign: 'center',
                marginBottom: screens.md ? '24px' : '16px',
                padding: screens.md ? '16px' : '12px'
            }}>
                <Title level={screens.md ? 2 : 3} style={{ margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <ClockCircleOutlined style={{ color: '#1890ff' }} />
                    Read Later
                </Title>
                <Text type="secondary" style={{ fontSize: screens.md ? '16px' : '14px' }}>
                    Your saved articles for later reading.
                </Text>
            </div>

            <div style={{
                padding: screens.md ? '24px' : '16px',
                maxWidth: screens.md ? '1000px' : '100%',
                margin: '0 auto'
            }}>
                <Space direction="vertical" size={screens.md ? "large" : "middle"} style={{ width: '100%' }}>

                {readLaterItems.length > 0 ? (
                    <>
                        <div>
                            {readLaterItems.map((item) => (
                                <ReadLaterItemCard 
                                    key={item.id} 
                                    item={item}
                                    onMarkAsRead={handleMarkAsRead}
                                    onRemove={handleRemoveFromReadLater}
                                    isMobile={!screens.md}
                                />
                            ))}
                        </div>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div style={{ 
                                textAlign: 'center', 
                                marginTop: screens.md ? '24px' : '16px' 
                            }}>
                                <Pagination
                                    current={pagination.page}
                                    total={pagination.total}
                                    pageSize={pagination.pageSize}
                                    showSizeChanger={false}
                                    onChange={handlePageChange}
                                    showQuickJumper={screens.md}
                                    size={screens.md ? "default" : "small"}
                                    showTotal={(total, range) =>
                                        screens.md 
                                            ? `${range[0]}-${range[1]} of ${total} articles`
                                            : `${range[0]}-${range[1]} / ${total}`
                                    }
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <Card>
                        <div style={{ textAlign: 'center', padding: '48px 0' }}>
                            <ClockCircleOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                            <Title level={3} type="secondary">
                                No articles in read later
                            </Title>
                            <Text type="secondary">
                                Articles you save for later reading will appear here.
                            </Text>
                            <div style={{ marginTop: '16px' }}>
                                <Button type="primary" onClick={() => navigate('/')}>
                                    Browse Articles
                                </Button>
                            </div>
                        </div>
                    </Card>
                )}
                </Space>
            </div>
        </div>
    );
};

export default ReadLaterPage; 